/**
 * Backend-owned Playground execution policy.
 *
 * SYNC: this file is copied byte-for-byte to
 * `progsity-web/features/playground/config/backend-language-contract.generated.ts`.
 * Run `node scripts/sync-playground-language-contract.mjs --write` from the
 * workspace root after changing it, then run the command without `--write`.
 *
 * **`PG-IDE01` replaced the single `runtime` display string with a version
 * table** (`docs/24_PLAYGROUND_SPEC.md` §3.1). The old field was one string doing
 * three jobs — the language's name, the toolchain, and the version — so nothing
 * could pin it, compare it, or fall back from it. A version an exam can pin has
 * to be an identifier the server validates, and the string a student reads has to
 * be derived from that row rather than parsed back out of it.
 *
 * **`EX-PUB04B` gave every version row a per-engine block** (`docs/24` §3.4),
 * because the Playground was then served by two engines with different
 * toolchains at different versions. Since 2026-09-06 (ADR-0058) the runner is
 * the only engine and a row holds one block, `runner`, or `null` where the
 * runner does not carry the id. Three properties survive that shape, and each
 * is load-bearing:
 *
 * - **The id is engine-independent.** `c++20`, `c11`, `3.11` name a standard or
 *   a release, never a toolchain, and are what a `SavedSnippet` pins and an
 *   exam constrains. Only the toolchain behind the id moves.
 * - **The id is not the flag, and the contract pins no flag.** The runner's own
 *   language table owns every token on its command line (`-O2`, `-std=`, `-lm`
 *   live there) and `POST /v1/compile` has no option field, so a version row
 *   carries no `compilerOptions` and the type forbids one. The engine this
 *   replaced pinned `-std=c++2a` for `c++20` on g++ 9.2.0; gcc 12 takes the real
 *   flag and reports `__cplusplus` 202002 (`docs/27` §8.3.7 check 4b).
 * - **A retired pin maps up rather than vanishing.** Eight rows are kept with
 *   `runner: null` and a `supersededBy`, so a snippet saved on `python/3.8` runs
 *   on `3.11` and the response says so out loud (2026-08-18 founder decision,
 *   point 5; ADR-0041 decision 8). A new write naming one is refused with the
 *   version the runner does offer, so the set of stale pins can only shrink.
 *
 * Every `runner` toolchain string below was probed **inside a box** on
 * `progsity-judge1-sgp1-01` against image `sha-7deca9f4f197`, the `EX-PUB04A`
 * production run recorded in `docs/27` §8.3.7. They are measurements, not the
 * runner table's own display strings, which name a Debian package rather than a
 * release.
 */

export const PLAYGROUND_LANGUAGE_CONTRACT_SCHEMA_VERSION = 4;

/**
 * Which engine is serving the Playground.
 *
 * One value since 2026-09-06 (ADR-0058). It stays a named id rather than being
 * dropped because health, the catalogue and every run response name the engine
 * that served them, and because the 2026-08-18 founder decision (point 2) fixed
 * the shape as one global switch for the whole surface, never per-language.
 */
export type PlaygroundEngineId = "runner";

export const PLAYGROUND_ENGINE_IDS: readonly PlaygroundEngineId[] = ["runner"];

export interface PlaygroundRunnerVersionPolicy {
  /**
   * The deployed toolchain, probed **inside a box** on the judge host
   * (`docs/27` §8.3.7), not read off the runner table's own display string.
   */
  toolchain: string;
  /**
   * There is deliberately no `compilerOptions` here.
   *
   * `progsity-runner`'s table is the sole owner of its command line (`-O2`,
   * `-std=`, `-lm` and the rest live there), and `POST /v1/compile` accepts no
   * option field at all. A second declaration here would be exactly the
   * two-sources-of-truth defect `PG-IDE01` removed from the request dimension,
   * and `docs/27` §8.3.7 is the standing evidence that an option declared where
   * nothing executes it is an option that turns out to be wrong: TypeScript's
   * `--lib ES2022` made `console.log` a type error and Go's `GOFLAGS=-mod=mod`
   * was dead config, both discovered only when the toolchain finally shipped.
   */
  readonly compilerOptions?: never;
}

export interface PlaygroundLanguageVersionPolicy {
  /**
   * What a client may send, and the only version token this system compares.
   *
   * Deliberately the *language standard* rather than the toolchain build, so it
   * is shared vocabulary with `progsity-runner`'s table (`c++17`, `c17`) and an
   * exam that pins "C++17 only" means the same thing on either engine.
   */
  id: string;
  /** Display, derived from this row. Nothing ever parses it back. */
  label: string;
  enabled: boolean;
  /**
   * How `progsity-runner` honours this id, or `null` when it cannot.
   *
   * A `null` row is a retired pin: it stays in the table for the read path so a
   * stored snippet still resolves, and it must name a `supersededBy`.
   */
  runner: PlaygroundRunnerVersionPolicy | null;
  /**
   * The version that replaces this one when the runner does not carry it.
   *
   * Declared rather than inferred. Every one of the eight retired pins happens to
   * map to the runner's default version, and leaning on that coincidence would
   * make an interpreter bump silently indistinguishable from a pin that simply
   * disappeared. They are different facts and they get different copy: "saved on
   * Python 3.8, ran on Python 3.11" against "3.8 is no longer available".
   */
  supersededBy: string | null;
}

/** The default a request or a snippet gets when it names no version. */
export type PlaygroundDefaultVersionIds = Record<PlaygroundEngineId, string>;

/**
 * User-selectable flags, exact tokens, per engine.
 *
 * Empty for every language, and structurally so: the runner's compile request
 * carries no option field, so there is nothing a token could be put into. The
 * `-std=` family in particular is never a request option: a language standard
 * is a version, and holding it in both dimensions is how a picker that says
 * C++17 ends up compiling C++20. The validator keeps the shape so a future
 * engine that does take options declares them here and nowhere else.
 */
export type PlaygroundAllowedCompilerOptions = Record<
  PlaygroundEngineId,
  readonly string[]
>;

export interface PlaygroundLanguagePolicy {
  id: string;
  enabled: boolean;
  defaultVersionId: PlaygroundDefaultVersionIds;
  versions: readonly PlaygroundLanguageVersionPolicy[];
  allowedCompilerOptions: PlaygroundAllowedCompilerOptions;
}

export interface PlaygroundLanguageContract {
  schemaVersion: number;
  languages: readonly PlaygroundLanguagePolicy[];
}

export const PLAYGROUND_LANGUAGE_CONTRACT = {
  schemaVersion: PLAYGROUND_LANGUAGE_CONTRACT_SCHEMA_VERSION,
  languages: [
    {
      id: "c",
      enabled: true,
      // Also the compiler's own default (`__STDC_VERSION__` 201710, measured),
      // so an unpinned program keeps compiling exactly as before.
      defaultVersionId: { runner: "c17" },
      versions: [
        {
          id: "c99",
          label: "C99",
          enabled: true,
          runner: { toolchain: "GCC 12.2.0" },
          supersededBy: null,
        },
        {
          id: "c11",
          label: "C11",
          enabled: true,
          runner: { toolchain: "GCC 12.2.0" },
          supersededBy: null,
        },
        {
          id: "c17",
          label: "C17",
          enabled: true,
          runner: { toolchain: "GCC 12.2.0" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: {
        // The runner compiles C as `gcc -O2 -std=<pin> -o prog main.c -lm`, fixed
        // in its own table. `-O2` and `-lm` are therefore already applied, and
        // the `-O0`, `-O1`, `-Wall` and `-Wextra` the previous engine allowed
        // have nowhere to go (`docs/24` §3.4).
        runner: [],
      },
    },
    {
      id: "cpp",
      enabled: true,
      // The engine the runner replaced defaulted to C++14 (`__cplusplus`
      // 201402, measured), so this was a deliberate behaviour change at the
      // flip: it aligns the Playground with `progsity-runner`'s `cpp` default,
      // which is the whole point of pinning versions at all. A student who
      // needs the old default pins `c++14`.
      defaultVersionId: { runner: "c++17" },
      versions: [
        {
          id: "c++14",
          label: "C++14",
          enabled: true,
          runner: { toolchain: "GCC 12.2.0" },
          supersededBy: null,
        },
        {
          id: "c++17",
          label: "C++17",
          enabled: true,
          runner: { toolchain: "GCC 12.2.0" },
          supersededBy: null,
        },
        {
          id: "c++20",
          label: "C++20",
          enabled: true,
          // The flag is not the id, and this row is the proof. g++ 9.2.0 answered
          // `-std=c++20` with "unrecognized command line option ... did you mean
          // '-std=c++2a'?" and reported `__cplusplus` 201709, the draft. gcc 12
          // takes the real flag and reports 202002. Same id, same student
          // intent, two toolchains honouring it differently, which is why the
          // caveat lived on the engine's half of the row and never on the id.
          runner: { toolchain: "GCC 12.2.0" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "python",
      enabled: true,
      defaultVersionId: { runner: "3.11" },
      versions: [
        {
          id: "3.8",
          label: "Python 3.8",
          enabled: true,
          runner: null,
          supersededBy: "3.11",
        },
        {
          id: "3.11",
          label: "Python 3.11",
          enabled: true,
          runner: { toolchain: "CPython 3.11.2" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "java",
      enabled: true,
      defaultVersionId: { runner: "17" },
      versions: [
        {
          id: "13",
          label: "Java 13",
          enabled: true,
          runner: null,
          supersededBy: "17",
        },
        {
          id: "17",
          label: "Java 17",
          enabled: true,
          runner: { toolchain: "OpenJDK 17.0.20" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "javascript",
      enabled: true,
      defaultVersionId: { runner: "22" },
      versions: [
        {
          id: "12",
          label: "Node.js 12",
          enabled: true,
          runner: null,
          supersededBy: "22",
        },
        {
          id: "22",
          label: "Node.js 22",
          enabled: true,
          runner: { toolchain: "Node.js 22.23.2" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "typescript",
      enabled: true,
      defaultVersionId: { runner: "5.6" },
      versions: [
        {
          id: "3.7",
          label: "TypeScript 3.7",
          enabled: true,
          runner: null,
          supersededBy: "5.6",
        },
        {
          id: "5.6",
          label: "TypeScript 5.6",
          enabled: true,
          // The runner compiles with a plain `tsc main.ts`, the same as the
          // engine it replaced. `docs/27` §8.3.7 removed the declared `--lib ES2022` because
          // naming a library set *replaces* tsc's default for the target, and
          // the default is what carries `console`: the spec as written made
          // `console.log` a type error (TS2584) and would have rejected programs
          // that compile on the engine being replaced.
          runner: { toolchain: "TypeScript 5.6.3 on Node.js 22.23.2" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "go",
      enabled: true,
      defaultVersionId: { runner: "1.19" },
      versions: [
        {
          id: "1.13",
          label: "Go 1.13",
          enabled: true,
          runner: null,
          supersededBy: "1.19",
        },
        {
          id: "1.19",
          label: "Go 1.19",
          enabled: true,
          runner: { toolchain: "Go 1.19.8" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "rust",
      enabled: true,
      defaultVersionId: { runner: "1.63" },
      versions: [
        {
          id: "1.40",
          label: "Rust 1.40",
          enabled: true,
          runner: null,
          supersededBy: "1.63",
        },
        {
          id: "1.63",
          label: "Rust 1.63",
          enabled: true,
          runner: { toolchain: "Rust 1.63.0" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "php",
      enabled: true,
      defaultVersionId: { runner: "8.2" },
      versions: [
        {
          id: "7.4",
          label: "PHP 7.4",
          enabled: true,
          runner: null,
          supersededBy: "8.2",
        },
        {
          id: "8.2",
          label: "PHP 8.2",
          enabled: true,
          runner: { toolchain: "PHP 8.2.33" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
    {
      id: "ruby",
      enabled: true,
      defaultVersionId: { runner: "3.1" },
      versions: [
        {
          id: "2.7",
          label: "Ruby 2.7",
          enabled: true,
          runner: null,
          supersededBy: "3.1",
        },
        {
          id: "3.1",
          label: "Ruby 3.1",
          enabled: true,
          runner: { toolchain: "Ruby 3.1.2p20" },
          supersededBy: null,
        },
      ],
      allowedCompilerOptions: { runner: [] },
    },
  ],
} as const satisfies PlaygroundLanguageContract;

export type PlaygroundLanguageId =
  (typeof PLAYGROUND_LANGUAGE_CONTRACT.languages)[number]["id"];

const policyById = new Map<string, PlaygroundLanguagePolicy>(
  PLAYGROUND_LANGUAGE_CONTRACT.languages.map((language) => [language.id, language]),
);

export function getLanguagePolicyById(
  id: string,
): PlaygroundLanguagePolicy | undefined {
  return policyById.get(id);
}

/** True when this row is live *and* the engine declares a way to honour it. */
export function isVersionOnEngine(
  version: PlaygroundLanguageVersionPolicy,
  engine: PlaygroundEngineId,
): boolean {
  return version.enabled && version[engine] !== null;
}

/**
 * The toolchain string for a row on one engine, or `null` when it has none.
 *
 * A version row now holds two toolchains, so every display path has to say which
 * engine it is describing. Nothing derives a toolchain any other way.
 */
export function getVersionToolchain(
  version: PlaygroundLanguageVersionPolicy,
  engine: PlaygroundEngineId,
): string | null {
  return version[engine]?.toolchain ?? null;
}

/**
 * Server-pinned tokens for a row on one engine. Always empty: the runner's own
 * table owns its command line. Kept as the one place a future engine that does
 * pin a token would answer from, so callers never read a row's block directly.
 */
export function getVersionCompilerOptions(
  version: PlaygroundLanguageVersionPolicy,
  engine: PlaygroundEngineId,
): readonly string[] {
  return version[engine]?.compilerOptions ?? [];
}

export function getAllowedCompilerOptions(
  languageId: string,
  engine: PlaygroundEngineId,
): readonly string[] {
  return getLanguagePolicyById(languageId)?.allowedCompilerOptions[engine] ?? [];
}

export function isCompilerOptionAllowed(
  languageId: string,
  option: string,
  engine: PlaygroundEngineId,
): boolean {
  return getAllowedCompilerOptions(languageId, engine).includes(option);
}

/** Selectable rows on one engine. A disabled or absent row stays readable. */
export function listLanguageVersions(
  languageId: string,
  engine: PlaygroundEngineId,
): readonly PlaygroundLanguageVersionPolicy[] {
  return (
    getLanguagePolicyById(languageId)?.versions.filter((version) =>
      isVersionOnEngine(version, engine),
    ) ?? []
  );
}

export interface PlaygroundVersionOption {
  id: string;
  label: string;
  toolchain: string;
  isDefault: boolean;
}

/**
 * What a picker renders for one engine.
 *
 * One function rather than a display shape per consumer: the runtime catalogue,
 * the web's settings panel and the verification scripts all need the same four
 * fields, and the toolchain among them is the one that moves with the engine.
 */
export function listLanguageVersionOptions(
  languageId: string,
  engine: PlaygroundEngineId,
): readonly PlaygroundVersionOption[] {
  const policy = getLanguagePolicyById(languageId);
  if (!policy) return [];
  return policy.versions
    .filter((version) => isVersionOnEngine(version, engine))
    .map((version) => ({
      id: version.id,
      label: version.label,
      toolchain: getVersionToolchain(version, engine) ?? "",
      isDefault: version.id === policy.defaultVersionId[engine],
    }));
}

export function getLanguageVersion(
  languageId: string,
  versionId: string,
): PlaygroundLanguageVersionPolicy | undefined {
  return getLanguagePolicyById(languageId)?.versions.find(
    (version) => version.id === versionId,
  );
}

/** True only for a version a client is allowed to select on this engine right now. */
export function isLanguageVersionSelectable(
  languageId: string,
  versionId: string,
  engine: PlaygroundEngineId,
): boolean {
  const version = getLanguageVersion(languageId, versionId);
  return version !== undefined && isVersionOnEngine(version, engine);
}

export interface ResolvedPlaygroundVersion {
  version: PlaygroundLanguageVersionPolicy;
  engine: PlaygroundEngineId;
  /** The active engine's toolchain for the row that will actually run. */
  toolchain: string;
  /** The active engine's pinned tokens. Empty on the runner, always. */
  compilerOptions: readonly string[];
  /**
   * The id that was asked for and could not be honoured *by anything*, or `null`.
   *
   * The point of returning it rather than swallowing it: a saved snippet whose
   * version disappeared has to fall back **visibly**, and a silent fallback is
   * the same class of bug as a picker that cannot express "C++17 only".
   */
  fellBackFrom: string | null;
  /**
   * The retired id this run was mapped **up** from, or `null`.
   *
   * Distinct from {@link fellBackFrom} because it is a different fact and needs
   * different copy. A pin that vanished is an apology; an interpreter bump is an
   * announcement, and the 2026-08-18 decision (point 5) requires it be made.
   */
  supersededFrom: string | null;
}

/**
 * Resolve a requested version on one engine, never failing when the language
 * itself is valid.
 *
 * Three outcomes, in the order they are tried: the engine has the requested id;
 * the id is retired and names a successor the engine does have (map up, and say
 * so); nothing matches, so the engine's default runs and the dropped id is
 * reported. Callers that must *refuse* an unknown version — the run boundary,
 * snippet writes — check {@link isLanguageVersionSelectable} first; callers that
 * must keep working — reading a snippet saved months ago, on either side of a
 * switch flip — use this.
 */
export function resolvePlaygroundVersion(
  languageId: string,
  requestedId: string | null | undefined,
  engine: PlaygroundEngineId,
): ResolvedPlaygroundVersion | undefined {
  const policy = getLanguagePolicyById(languageId);
  if (!policy) return undefined;

  const onEngine = policy.versions.filter((version) => isVersionOnEngine(version, engine));
  const fallback =
    onEngine.find((version) => version.id === policy.defaultVersionId[engine]) ?? onEngine[0];
  if (!fallback) return undefined;

  const describe = (
    version: PlaygroundLanguageVersionPolicy,
    fellBackFrom: string | null,
    supersededFrom: string | null,
  ): ResolvedPlaygroundVersion => ({
    version,
    engine,
    toolchain: getVersionToolchain(version, engine) ?? "",
    compilerOptions: getVersionCompilerOptions(version, engine),
    fellBackFrom,
    supersededFrom,
  });

  const requested = requestedId?.trim();
  if (!requested) return describe(fallback, null, null);

  const match = onEngine.find((version) => version.id === requested);
  if (match) return describe(match, null, null);

  // Declared somewhere in the table, just not on this engine: that is the
  // interpreter bump, and it has a named successor rather than a guess.
  const retired = policy.versions.find((version) => version.id === requested);
  const successor = retired?.supersededBy
    ? onEngine.find((version) => version.id === retired.supersededBy)
    : undefined;
  if (successor) return describe(successor, null, requested);

  return describe(fallback, requested, null);
}

export interface PlaygroundVersionView {
  id: string;
  label: string;
  toolchain: string;
  /** The pin that could not be honoured, so a client can say so out loud. */
  fellBackFrom: string | null;
  /** The retired pin this superseded, so a client can say "saved on X, ran on Y". */
  supersededFrom: string | null;
}

/**
 * A stored or requested version, rendered for a human on one engine.
 *
 * Lives with the contract rather than with a service because it derives nothing
 * the contract does not already hold — and because the byte-synced web copy then
 * renders a snippet's version from the same rows the backend validated it
 * against, instead of a second table that can disagree.
 */
export function describeLanguageVersion(
  languageId: string,
  requestedId: string | null | undefined,
  engine: PlaygroundEngineId,
): PlaygroundVersionView | null {
  const resolved = resolvePlaygroundVersion(languageId, requestedId, engine);
  if (!resolved) return null;
  return {
    id: resolved.version.id,
    label: resolved.version.label,
    toolchain: resolved.toolchain,
    fellBackFrom: resolved.fellBackFrom,
    supersededFrom: resolved.supersededFrom,
  };
}

const LANGUAGE_KEY_PATTERN = /^[a-z][a-z0-9-]*$/;
const COMPILER_OPTION_PATTERN = /^-[a-zA-Z0-9+_.=:-]+$/;
/** Wide enough for `c++17` and `1.13`, narrow enough to stay an opaque token. */
const VERSION_ID_PATTERN = /^[a-z0-9][a-z0-9+._-]*$/;
const VERSION_ID_MAX_LENGTH = 40;

/** Returns actionable errors so the same check can run in backend and web CI. */
export function validatePlaygroundLanguageContract(
  contract: PlaygroundLanguageContract,
): string[] {
  const errors: string[] = [];
  if (contract.schemaVersion !== PLAYGROUND_LANGUAGE_CONTRACT_SCHEMA_VERSION) {
    errors.push(
      `Stale schemaVersion ${contract.schemaVersion}; expected ${PLAYGROUND_LANGUAGE_CONTRACT_SCHEMA_VERSION}. Sync the generated web contract.`,
    );
  }
  if (contract.languages.length === 0) {
    errors.push("At least one language policy is required.");
  }

  const keys = new Set<string>();
  for (const language of contract.languages) {
    if (!LANGUAGE_KEY_PATTERN.test(language.id)) {
      errors.push(`Language key "${language.id}" must match ${LANGUAGE_KEY_PATTERN}.`);
    }
    if (keys.has(language.id)) {
      errors.push(`Duplicate language key "${language.id}".`);
    }
    keys.add(language.id);

    if (typeof language.enabled !== "boolean") {
      errors.push(`Language "${language.id}" must declare enabled as a boolean.`);
    }

    const allowedByEngine = new Map<PlaygroundEngineId, Set<string>>();
    for (const engine of PLAYGROUND_ENGINE_IDS) {
      const allowedOptions = new Set<string>();
      for (const option of language.allowedCompilerOptions[engine] ?? []) {
        if (!COMPILER_OPTION_PATTERN.test(option)) {
          errors.push(
            `Language "${language.id}" has unsafe ${engine} compiler option "${option}". Options must be exact single tokens.`,
          );
        }
        if (allowedOptions.has(option)) {
          errors.push(
            `Language "${language.id}" has duplicate ${engine} compiler option "${option}".`,
          );
        }
        allowedOptions.add(option);
      }
      allowedByEngine.set(engine, allowedOptions);
    }

    // The runner takes no options at all: `POST /v1/compile` has no field for
    // one, so a token declared here could only ever be silently dropped.
    for (const option of language.allowedCompilerOptions.runner ?? []) {
      errors.push(
        `Language "${language.id}" declares runner compiler option "${option}". The runner's own language table owns its command line and its compile request carries no option field.`,
      );
    }

    errors.push(...validateLanguageVersions(language, allowedByEngine));
  }

  return errors;
}

function validateLanguageVersions(
  language: PlaygroundLanguagePolicy,
  allowedByEngine: ReadonlyMap<PlaygroundEngineId, ReadonlySet<string>>,
): string[] {
  const errors: string[] = [];

  if (language.versions.length === 0) {
    errors.push(`Language "${language.id}" declares no runtime versions.`);
    return errors;
  }

  const versionIds = new Set<string>();
  for (const version of language.versions) {
    const label = `Language "${language.id}" version "${version.id}"`;

    if (!VERSION_ID_PATTERN.test(version.id) || version.id.length > VERSION_ID_MAX_LENGTH) {
      errors.push(
        `${label} must match ${VERSION_ID_PATTERN} and be at most ${VERSION_ID_MAX_LENGTH} characters.`,
      );
    }
    if (versionIds.has(version.id)) {
      errors.push(`Language "${language.id}" has duplicate version "${version.id}".`);
    }
    versionIds.add(version.id);

    if (!version.label.trim()) errors.push(`${label} is missing a display label.`);
    if (typeof version.enabled !== "boolean") {
      errors.push(`${label} must declare enabled as a boolean.`);
    }

    if (version.runner === null && version.supersededBy === null) {
      errors.push(
        `${label} is declared on no engine and names no supersededBy, so nothing can ever run it. A retired pin must map up by name.`,
      );
    }
    if (version.runner !== null && !version.runner.toolchain.trim()) {
      errors.push(`${label} is missing runner toolchain metadata.`);
    }

    const pinned = new Set<string>();
    for (const option of getVersionCompilerOptions(version, "runner")) {
      if (!COMPILER_OPTION_PATTERN.test(option)) {
        errors.push(
          `${label} pins unsafe compiler option "${option}". Options must be exact single tokens.`,
        );
      }
      if (pinned.has(option)) {
        errors.push(`${label} pins duplicate compiler option "${option}".`);
      }
      pinned.add(option);
      if (allowedByEngine.get("runner")?.has(option)) {
        // The two-sources-of-truth bug, caught in CI rather than by a student
        // whose picker says C++17 and whose program compiles as C++20.
        errors.push(
          `${label} pins "${option}", which is also user-selectable on "${language.id}". A token belongs to the version or to the request, never to both.`,
        );
      }
    }

    if (version.supersededBy !== null) {
      const successor = language.versions.find(
        (candidate) => candidate.id === version.supersededBy,
      );
      if (!successor) {
        errors.push(
          `${label} is superseded by "${version.supersededBy}", which "${language.id}" does not declare.`,
        );
      } else if (successor.id === version.id) {
        errors.push(`${label} is superseded by itself.`);
      } else if (!successor.enabled) {
        errors.push(
          `${label} is superseded by "${successor.id}", which is disabled. A retired pin must map up to something that runs.`,
        );
      }
    }
  }

  for (const engine of PLAYGROUND_ENGINE_IDS) {
    const onEngine = language.versions.filter((version) => isVersionOnEngine(version, engine));
    const fallback = language.versions.find(
      (version) => version.id === language.defaultVersionId[engine],
    );
    if (!fallback) {
      errors.push(
        `Language "${language.id}" has ${engine} defaultVersionId "${language.defaultVersionId[engine]}", which it does not declare.`,
      );
    } else if (language.enabled && !isVersionOnEngine(fallback, engine)) {
      errors.push(
        `Language "${language.id}" is enabled but its ${engine} default version "${language.defaultVersionId[engine]}" does not run on ${engine}.`,
      );
    }

    if (language.enabled && onEngine.length === 0) {
      errors.push(`Language "${language.id}" is enabled with no version on ${engine}.`);
    }
  }

  /**
   * The map-up rule.
   *
   * A live row the runner cannot serve is a *retired pin* and has to name its
   * successor: otherwise a stored `python/3.8` resolves to an unexplained
   * default, which is precisely what the 2026-08-18 decision (point 5) forbids.
   * The successor must itself run on the runner, so a chain of retirements can
   * never end in a row nothing serves.
   */
  for (const version of language.versions) {
    if (!version.enabled || version.runner !== null) continue;
    if (version.supersededBy === null) {
      errors.push(
        `Language "${language.id}" version "${version.id}" does not run on the runner and names no supersededBy. A retired pin must map up by name, not fall through to the default.`,
      );
      continue;
    }
    const successor = language.versions.find(
      (candidate) => candidate.id === version.supersededBy,
    );
    if (successor && !isVersionOnEngine(successor, "runner")) {
      errors.push(
        `Language "${language.id}" version "${version.id}" is superseded by "${successor.id}", which does not run on the runner either.`,
      );
    }
  }

  return errors;
}
